import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/client';
import {
  WEEKDAY_FULL,
  WEEKDAY_NAMES,
  addDaysISO,
  formatDateShort,
  formatTimeFromISO,
  minutesFromMidnight,
  mondayOf,
  moscowDateISO,
  occurrenceDateMoscow,
  timeFromOccurrenceStart,
  weekDates,
} from '../utils/scheduleDates';

const HOUR_HEIGHT = 40;

export default function PublicSchedulePage() {
  const { token } = useParams();
  const [viewMode, setViewMode] = useState('template');
  const [weekStart, setWeekStart] = useState(() => mondayOf(moscowDateISO()));
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/scheduler/public/${token}/`, {
          params: { week: weekStart },
        });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || 'Ссылка недействительна');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, weekStart]);

  const workStart = data?.work_start || '11:00';
  const workEnd = data?.work_end || '21:00';
  const gridStartMin = minutesFromMidnight(workStart);
  const gridEndMin = Math.max(minutesFromMidnight(workEnd), gridStartMin + 60);
  const gridHours = Math.ceil((gridEndMin - gridStartMin) / 60);
  const days = useMemo(() => weekDates(weekStart), [weekStart]);

  const occByDay = useMemo(() => {
    const map = Object.fromEntries(days.map((d) => [d, []]));
    for (const occ of data?.occurrences || []) {
      const d = occurrenceDateMoscow(occ.start);
      if (map[d]) map[d].push(occ);
    }
    return map;
  }, [data, days]);

  const templateByWeekday = useMemo(() => {
    const map = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
    for (const slot of data?.weekly_pattern || []) {
      map[slot.weekday]?.push(slot);
    }
    return map;
  }, [data]);

  const hourLabels = Array.from({ length: gridHours + 1 }, (_, i) => {
    const mins = gridStartMin + i * 60;
    const h = Math.floor(mins / 60);
    return `${String(h).padStart(2, '0')}:00`;
  });

  function cellStyle(startHHMM, duration) {
    const startMin = minutesFromMidnight((startHHMM || '00:00').slice(0, 5));
    const top = ((startMin - gridStartMin) / 60) * HOUR_HEIGHT;
    const height = Math.max((duration / 60) * HOUR_HEIGHT, 24);
    return { top: `${top}px`, height: `${height}px` };
  }

  return (
    <div className="sched-page sched-page--public">
      <div className="page-header">
        <h1>Занятость преподавателя</h1>
        <p className="page-subtitle">
          Без имён. <strong>Расписание</strong> — постоянные слоты; <strong>календарь</strong> — выбранная неделя
          (включая отмены).
        </p>
      </div>

      <div className="sched-mode-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          className={`sched-mode-tab${viewMode === 'template' ? ' is-active' : ''}`}
          onClick={() => setViewMode('template')}
        >
          Расписание
        </button>
        <button
          type="button"
          role="tab"
          className={`sched-mode-tab${viewMode === 'calendar' ? ' is-active' : ''}`}
          onClick={() => setViewMode('calendar')}
        >
          Календарь недели
        </button>
      </div>

      {viewMode === 'calendar' && (
        <div className="sched-toolbar">
          <div className="sched-week-nav">
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setWeekStart(addDaysISO(weekStart, -7))}>
              ←
            </button>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => setWeekStart(mondayOf(moscowDateISO()))}
            >
              Сегодня
            </button>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setWeekStart(addDaysISO(weekStart, 7))}>
              →
            </button>
            <span className="sched-week-label">
              {formatDateShort(weekStart)} — {formatDateShort(addDaysISO(weekStart, 6))}
            </span>
          </div>
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}
      {loading && <p>Загрузка…</p>}

      {!loading && data && (
        <>
          <div className="sched-grid-wrap">
            <div className="sched-grid">
              <div className="sched-grid-corner" />
              {(viewMode === 'template' ? WEEKDAY_NAMES : days.map((_, i) => WEEKDAY_NAMES[i])).map((name, i) => (
                <div key={name + i} className="sched-day-head">
                  <div className="sched-day-name">{name}</div>
                  <div className="sched-day-date">
                    {viewMode === 'template' ? 'каждую неделю' : formatDateShort(days[i])}
                  </div>
                </div>
              ))}
              <div className="sched-hours">
                {hourLabels.map((label) => (
                  <div key={label} className="sched-hour-label" style={{ height: HOUR_HEIGHT }}>
                    {label}
                  </div>
                ))}
              </div>
              {(viewMode === 'template' ? [0, 1, 2, 3, 4, 5, 6] : days).map((key, idx) => {
                const colKey = viewMode === 'template' ? key : key;
                const items =
                  viewMode === 'template'
                    ? templateByWeekday[key] || []
                    : occByDay[key] || [];
                return (
                  <div key={String(colKey) + idx} className="sched-day-col" style={{ height: gridHours * HOUR_HEIGHT }}>
                    {hourLabels.slice(0, -1).map((label) => (
                      <div key={label} className="sched-hour-line" style={{ height: HOUR_HEIGHT }} />
                    ))}
                    {viewMode === 'template'
                      ? items.map((slot) => (
                          <div
                            key={slot.id}
                            className={`sched-occ sched-occ--public${slot.is_group ? ' sched-occ--group' : ''}`}
                            style={cellStyle(slot.start_time, slot.duration_minutes)}
                          >
                            <span className="sched-occ-people">
                              <span className="sched-occ-person">{slot.label}</span>
                            </span>
                            <span className="sched-occ-meta">
                              <span className="sched-occ-time">{slot.start_time}</span>
                              {slot.is_group && (
                                <span className="sched-occ-slots">
                                  {slot.enrolled_count}/{slot.max_students}
                                </span>
                              )}
                            </span>
                          </div>
                        ))
                      : items.map((occ) => {
                          const startMin = minutesFromMidnight(timeFromOccurrenceStart(occ.start));
                          const top = ((startMin - gridStartMin) / 60) * HOUR_HEIGHT;
                          const height = Math.max((occ.duration_minutes / 60) * HOUR_HEIGHT, 24);
                          return (
                            <div
                              key={occ.key}
                              className={`sched-occ sched-occ--public${occ.is_group ? ' sched-occ--group' : ''}${
                                occ.is_cancelled ? ' sched-occ--cancelled' : ''
                              }`}
                              style={{ top: `${top}px`, height: `${height}px` }}
                            >
                              <span className="sched-occ-people">
                                <span className="sched-occ-person">
                                  {occ.is_cancelled ? 'отмена' : occ.label}
                                </span>
                              </span>
                              <span className="sched-occ-meta">
                                <span className="sched-occ-time">{formatTimeFromISO(occ.start)}</span>
                              </span>
                            </div>
                          );
                        })}
                  </div>
                );
              })}
            </div>
          </div>

          {viewMode === 'template' && (
            <section className="sched-panel">
              <h2>Свободное время (по шаблону)</h2>
              <pre className="sched-free-text">{data.free_text}</pre>
              <p className="muted" style={{ marginTop: '0.75rem' }}>
                Разовая отмена на одной неделе не делает слот свободным «навсегда» — в шаблоне он остаётся занятым.
              </p>
            </section>
          )}

          {viewMode === 'calendar' && (
            <p className="sched-legend muted">
              Зачёркнутое — отмена только на эту дату. В обычном расписании слот по-прежнему занят
              ({WEEKDAY_FULL[0]}…{WEEKDAY_FULL[6]}).
            </p>
          )}
        </>
      )}
    </div>
  );
}
